import React from "react";

export type SwitchProps = {
  checked?: boolean;
  defaultChecked?: boolean;
  disabled?: boolean;
  onCheckedChange?: (checked: boolean) => void;
  className?: string;
  id?: string;
  name?: string;
};

export const Switch: React.FC<SwitchProps> = ({
  checked,
  defaultChecked,
  disabled,
  onCheckedChange,
  className = "",
  id,
  name,
}) => {
  const isControlled = typeof checked === "boolean";
  const [internal, setInternal] = React.useState<boolean>(!!defaultChecked);
  const isOn = isControlled ? (checked as boolean) : internal;

  const toggle = () => {
    if (disabled) return;
    const next = !isOn;
    if (!isControlled) setInternal(next);
    onCheckedChange?.(next);
  };

  const onKeyDown: React.KeyboardEventHandler<HTMLButtonElement> = (e) => {
    if (disabled) return;
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      toggle();
    }
  };

  return (
    <div className="inline-flex items-center gap-2">
      {/* Hidden input for forms (optional) */}
      {name ? (
        <input type="hidden" name={name} value={isOn ? "true" : "false"} />
      ) : null}

      <button
        id={id}
        type="button"
        role="switch"
        aria-checked={isOn}
        aria-disabled={disabled || undefined}
        onClick={toggle}
        onKeyDown={onKeyDown}
        className={[
          "relative inline-flex h-7 w-12 items-center rounded-full transition-colors",
          "focus:outline-none focus:ring-4 focus:ring-white/30",
          disabled ? "opacity-60 cursor-not-allowed" : "cursor-pointer",
          isOn ? "bg-emerald-500" : "bg-slate-300",
          className,
        ].join(" ")}
      >
        <span
          className={[
            "inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform",
            isOn ? "translate-x-6" : "translate-x-1",
          ].join(" ")}
        />
      </button>
    </div>
  );
};

export default Switch;
